import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useReviewMode } from "../context/ReviewModeContext";

import { useMsal } from "@azure/msal-react";

import {
  FileText,
  Mail,
  Pencil,
  Clock3,
  Files,
  Eye,
  RefreshCw,
  Layers,
  AlertTriangle,
  Users,
  Check,
  Search,
  UserRound,
} from "lucide-react";

import { callApi } from "../api/api";
import { useAccess } from "../context/AccessContext";
import {
  assignedInitials,
  canSeeInboxGroups,
  canSeeInboxOverview,
  formatAssignedTo,
  inboxQueueEmptyCopy,
  isPendingQuickFilterActive,
  pendingQueueTitle,
  pendingQuickFilters,
  showInboxAssignedColumn,
  showInboxDepartmentColumn,
  verifiedQuickFilters,
} from "./inboxReviewScope";
import {
  buildInboxKpiCards,
  buildPendingActionItems,
  EMPTY_INBOX_SUMMARY,
  formatInboxCount,
  inboxKpiLayout,
} from "./inboxSummaryKpis";
import {
  avatarTone,
  departmentProgress,
  departmentTone,
  formatDashCount,
  inboxPageSubtitle,
  inboxScopeLine,
  metadataPillTone,
  namedDepartments,
  isUnassignedDepartmentName,
  listingDepartmentLabel,
  quickFilterCountLabel,
} from "./inboxReviewChrome";

import MetadataWorkspacePanel from "../components/MetadataWorkspacePanel";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";
import LoadingOverlay from "../components/LoadingOverlay";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";
import "./InboxReview.css";

const KPI_TONE = {
  pending: "pending",
  unassigned: "unassigned",
  assigned: "assigned",
  verified: "verified",
  older: "older",
  my_pending: "pending",
  my_older: "older",
  team_pending: "purple",
  my_verified: "verified",
  team_verified: "purple",
  verified_today: "verified",
  in_progress: "in_progress",
  period: "time",
};

function MetricSkel({ variant = "num" }) {
  return <span className={`ir-skel ir-skel--${variant}`} aria-hidden="true" />;
}

function InboxAvatar({ name }) {
  const initials = assignedInitials(name);
  if (!initials) {
    return <span className="ir-avatar ir-avatar--empty">—</span>;
  }
  return (
    <span className={`ir-avatar ir-avatar--${avatarTone(name)}`}>{initials}</span>
  );
}

function AssignedDonut({ pct }) {
  const n = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div
      className="ir-donut"
      style={{ background: `conic-gradient(#2563eb ${n}%, #e2e8f0 0)` }}
    >
      <div className="ir-donut__hole">
        <strong>{n}%</strong>
        <span>Assigned</span>
      </div>
    </div>
  );
}

function AssignedCell({ value }) {
  const label = formatAssignedTo(value);
  if (label === "Unassigned") {
    return <span className="ir-unassigned-pill">Unassigned</span>;
  }
  return (
    <div className="ir-assigned">
      <InboxAvatar name={value} />
      <span>{label}</span>
    </div>
  );
}

function DepartmentRichCard({ dept, onOpen }) {
  const tone = departmentTone(dept);
  const isUnassigned = dept.department_id === "unassigned";
  const progress = departmentProgress(dept);
  return (
    <button
      type="button"
      className={`ir-rich ir-rich--${tone}${isUnassigned ? " ir-rich--unassigned" : ""}`}
      onClick={onOpen}
    >
      <div className="ir-rich__head">
        <div>
          <div className="ir-rich__name">{dept.department_name}</div>
          <div className="ir-rich__code">
            {isUnassigned ? "Unassigned" : dept.department_code || dept.department_name}
          </div>
        </div>
        {isUnassigned ? (
          <span className="ir-rich__avatar">
            <UserRound size={14} />
          </span>
        ) : (
          <div className="ir-rich__members">
            <strong>{formatInboxCount(dept.member_count)}</strong>
            <span>Members</span>
          </div>
        )}
      </div>
      <div className="ir-rich__metrics">
        <div>
          <b className="ir-num--pending">{formatInboxCount(dept.pending_count)}</b>
          <span>Pending</span>
        </div>
        <div>
          <b className="ir-num--verified">{formatInboxCount(dept.verified_count)}</b>
          <span>Verified</span>
        </div>
        <div>
          <b className="ir-rich__older">{formatInboxCount(dept.older_than_24h)}</b>
          <span>Older &gt;24h</span>
        </div>
      </div>
      {!isUnassigned && (
        <div className="ir-rich__progress">
          <div className="ir-rich__progress-label">
            Assignment progress
            <strong>{progress.pct}%</strong>
          </div>
          <div className="ir-progress">
            <div
              className={`ir-progress__fill ir-progress__fill--${tone}`}
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <div className="ir-rich__progress-meta">
            <span>{progress.assignedPeople} with pending work</span>
          </div>
        </div>
      )}
      <div className="ir-queue-link">
        <Search size={13} />
        View Queue
      </div>
    </button>
  );
}

function DepartmentRichCardSkel() {
  return (
    <div className="ir-rich ir-rich--skel" aria-hidden="true">
      <div className="ir-rich__head">
        <div>
          <MetricSkel variant="pill" />
          <div style={{ marginTop: 8 }}>
            <MetricSkel variant="hint" />
          </div>
        </div>
        <MetricSkel variant="avatar" />
      </div>
      <div className="ir-rich__metrics">
        <div>
          <MetricSkel variant="num" />
          <span>Pending</span>
        </div>
        <div>
          <MetricSkel variant="num" />
          <span>Verified</span>
        </div>
        <div>
          <MetricSkel variant="num" />
          <span>Older &gt;24h</span>
        </div>
      </div>
    </div>
  );
}

export default function MetadataReview({
  embedMode = null,
  initialSelectedGroup = null,
  embedGroupsBackLabel = "Back to departments",
  initialQueueFilter = {},
  initialFromDate = "",
  initialToDate = "",
  embedQueueTitle = "Metadata Queue",
  embedQueueCountLabel = "",
  embedQueueBackLabel = "Back",
  onEmbedQueueBack,
}) {
  const { instance, accounts } = useMsal();
  const { hierarchyRoleCode, isAdmin } = useAccess();
  const isGroupsEmbed = embedMode === "groups";
  const isQueueEmbed = embedMode === "queue";
  const showGroupsTab = canSeeInboxGroups(hierarchyRoleCode, isAdmin);
  const effectiveShowGroupsTab = isGroupsEmbed || showGroupsTab;
  const showOverviewTab = canSeeInboxOverview(hierarchyRoleCode, isAdmin);
  const kpiLayout = inboxKpiLayout(hierarchyRoleCode, isAdmin);
  const showAssignedColumn = showInboxAssignedColumn(kpiLayout);

  const FILESYSTEM =
    process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";
  const { reviewMode, setReviewMode } = useReviewMode();

  const isSmallScreen = useBreakpoint(480);
  /* =========================================
     STATE
  ========================================= */

  const [activeTab, setActiveTab] = useState(
    isGroupsEmbed
      ? "groups"
      : isQueueEmbed
        ? initialQueueFilter.queueTab === "verified"
          ? "verified"
          : "pending"
        : showOverviewTab
          ? "overview"
          : "pending",
  );
  const [senderEmail, setSenderEmail] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedSenderEmail, setAppliedSenderEmail] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState(
    () => initialQueueFilter.from_date || initialFromDate || "",
  );
  const [appliedToDate, setAppliedToDate] = useState(
    () => initialQueueFilter.to_date || initialToDate || "",
  );
  const [assignedState, setAssignedState] = useState(
    isQueueEmbed ? initialQueueFilter.assigned_state || "all" : "all",
  );
  const [olderThan24h, setOlderThan24h] = useState(
    isQueueEmbed ? Boolean(initialQueueFilter.older_than_24h) : false,
  );
  const verifiedPeriod = isQueueEmbed
    ? initialQueueFilter.verified_period || ""
    : "";
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [pendingDepartmentId, setPendingDepartmentId] = useState("");
  const [managerView, setManagerView] = useState("team");

  /* ---------- PENDING ---------- */

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const pendingPageSize = 10;

  /* ---------- VERIFIED ---------- */

  const [verifiedRows, setVerifiedRows] = useState([]);

  const [verifiedLoading, setVerifiedLoading] = useState(false);

  const [verifiedPage, setVerifiedPage] = useState(1);

  const [verifiedTotal, setVerifiedTotal] = useState(0);

  const [expandedRows, setExpandedRows] = useState({});

  const [previewLoading, setPreviewLoading] = useState(false);

  const verifiedPageSize = 10;

  /* ---------- GROUPS ---------- */

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(
    isGroupsEmbed ? initialSelectedGroup : null,
  );
  const [selectedGroupPerson, setSelectedGroupPerson] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [groupWorkload, setGroupWorkload] = useState([]);
  const [summary, setSummary] = useState(EMPTY_INBOX_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const displayGroups = useMemo(() => {
    return (groups || []).filter((dept) => {
      if (!dept) return false;
      const name = String(dept.department_name || "").toLowerCase().trim();
      if (dept.department_id === "unassigned" || name === "unassigned" || name === "unassigned work") {
        // The analytics Department tab is department-wise only.
        if (isGroupsEmbed) return false;
        return Number(dept.pending_count || 0) > 0 || Number(dept.older_than_24h || 0) > 0;
      }
      return !isUnassignedDepartmentName(dept.department_name);
    });
  }, [groups, isGroupsEmbed]);

  /* ---------- COMMON ---------- */

  const [selectedRow, setSelectedRow] = useState(null);

  const [toast, setToast] = useState(null);
  const [isCompactTable, setIsCompactTable] = useState(false);
  const isNarrow = useBreakpoint(1024);
  const queueTopRef = useRef(null);
  const skipInitialPageScroll = useRef(true);

  const groupsTabActive = isGroupsEmbed || activeTab === "groups";
  const groupsSurfaceVisible = isGroupsEmbed || !reviewMode;
  const queueEmbedSurfaceVisible = isQueueEmbed && !selectedRow;

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });

    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactTable(window.innerWidth <= 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================================
     LOAD PENDING DATA
  ========================================= */

  const loadData = useCallback(
    async (signal) => {
      /*
      Prevent duplicate concurrent calls
    */

      try {
        setLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/pending",
          null,
          {
            signal,
            params: {
              page,
              pageSize: pendingPageSize,
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
              hierarchy_scope: 1,
              department_id:
                selectedGroup?.department_id ||
                (pendingDepartmentId && pendingDepartmentId !== "unassigned"
                  ? pendingDepartmentId
                  : undefined) ||
                undefined,
              assigned_state: assignedState !== "all" ? assignedState : undefined,
              older_than_24h: olderThan24h ? 1 : undefined,
              assignee_email:
                selectedGroupPerson?.email || assigneeEmail || undefined,
            },
          },
        );

        /*
        Ignore aborted requests
      */

        if (signal?.aborted) return;

        setRows(res?.data?.data || []);

        setTotal(res?.data?.total || 0);
      } catch (err) {
        /*
        Ignore cancellation errors
      */

        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load metadata review", err);

        setRows([]);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      page,
      appliedSenderEmail,
      appliedFromDate,
      appliedToDate,
      selectedGroup,
      assignedState,
      olderThan24h,
      assigneeEmail,
      pendingDepartmentId,
      selectedGroupPerson,
    ],
  );

  const loadGroups = useCallback(
    async (signal) => {
      try {
        setGroupsLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/inbox-groups",
          null,
          {
            signal,
            params: {
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
            },
          },
        );

        if (signal?.aborted) return;

        setGroups(res?.data?.data || []);
        setGroupWorkload(res?.data?.workload || []);
      } catch (err) {
        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load inbox groups", err);
        setGroups([]);
        setGroupWorkload([]);
      } finally {
        if (!signal?.aborted) {
          setGroupsLoading(false);
        }
      }
    },
    [instance, accounts, appliedSenderEmail, appliedFromDate, appliedToDate],
  );

  const loadGroupMembers = useCallback(
    async (signal) => {
      if (!selectedGroup?.department_id || selectedGroup.department_id === "unassigned") {
        setGroupMembers([]);
        return;
      }

      try {
        setGroupMembersLoading(true);
        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/inbox-group-members",
          null,
          {
            signal,
            params: {
              department_id: selectedGroup.department_id,
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
            },
          },
        );

        if (signal?.aborted) return;
        setGroupMembers(res?.data?.data || []);
      } catch (err) {
        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load group members", err);
        setGroupMembers([]);
      } finally {
        if (!signal?.aborted) {
          setGroupMembersLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      selectedGroup,
      appliedSenderEmail,
      appliedFromDate,
      appliedToDate,
    ],
  );

  const loadSummary = useCallback(
    async (signal) => {
      setSummaryLoading(true);
      try {
        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/inbox-summary",
          null,
          {
            signal,
            params: {
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
            },
          },
        );

        if (signal?.aborted) return;

        setSummary({
          ...EMPTY_INBOX_SUMMARY,
          ...(res?.data?.data || {}),
        });
      } catch (err) {
        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load inbox summary", err);
        setSummary(EMPTY_INBOX_SUMMARY);
      } finally {
        if (!signal?.aborted) {
          setSummaryLoading(false);
        }
      }
    },
    [instance, accounts, appliedSenderEmail, appliedFromDate, appliedToDate],
  );

  /* =========================================
     LOAD VERIFIED DATA
  ========================================= */

  const loadVerifiedData = useCallback(
    async (signal) => {
      /*
      Prevent duplicate concurrent calls
    */

      try {
        setVerifiedLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/verified",
          null,
          {
            signal,
            params: {
              page: verifiedPage,
              pageSize: verifiedPageSize,
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
              hierarchy_scope: 1,
              department_id:
                pendingDepartmentId && pendingDepartmentId !== "unassigned"
                  ? pendingDepartmentId
                  : undefined,
              assigned_state: assignedState !== "all" ? assignedState : undefined,
              assignee_email: assigneeEmail || undefined,
              verified_period: verifiedPeriod || undefined,
            },
          },
        );

        /*
        Ignore aborted requests
      */

        if (signal?.aborted) return;

        setVerifiedRows(res?.data?.data || []);

        setVerifiedTotal(res?.data?.total || 0);
      } catch (err) {
        /*
        Ignore cancellation errors
      */

        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load verified metadata", err);

        setVerifiedRows([]);
      } finally {
        if (!signal?.aborted) {
          setVerifiedLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      verifiedPage,
      appliedSenderEmail,
      appliedFromDate,
      appliedToDate,
      assignedState,
      assigneeEmail,
      pendingDepartmentId,
      verifiedPeriod,
    ],
  );

  /* =========================================
     EFFECTS
  ========================================= */

  useEffect(() => {
    if (!isGroupsEmbed) {
      return;
    }

    setActiveTab("groups");
    setSelectedGroup(initialSelectedGroup || null);
    setSelectedGroupPerson(null);
    setGroupMembers([]);
    setPage(1);
    setRows([]);
    setTotal(0);
  }, [isGroupsEmbed, initialSelectedGroup]);

  useEffect(() => {
    // A selected group always shows a queue: the whole department's under
    // its people list, or one person's after drilling in.
    const viewingPendingQueue =
      activeTab === "pending" || (groupsTabActive && selectedGroup);

    if (!accounts.length || !viewingPendingQueue) {
      return;
    }

    const controller = new AbortController();

    loadData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, activeTab, selectedGroup, selectedGroupPerson, loadData, groupsTabActive]);

  useEffect(() => {
    if (
      !accounts.length ||
      !groupsTabActive ||
      selectedGroup ||
      !effectiveShowGroupsTab
    ) {
      return;
    }

    const controller = new AbortController();

    loadGroups(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, groupsTabActive, selectedGroup, effectiveShowGroupsTab, loadGroups]);

  useEffect(() => {
    const viewingGroupPeople =
      groupsTabActive &&
      selectedGroup &&
      selectedGroup.department_id !== "unassigned" &&
      !selectedGroupPerson;

    if (!accounts.length || !viewingGroupPeople || !effectiveShowGroupsTab) {
      return;
    }

    const controller = new AbortController();
    loadGroupMembers(controller.signal);

    return () => {
      controller.abort();
    };
  }, [
    accounts.length,
    groupsTabActive,
    selectedGroup,
    selectedGroupPerson,
    effectiveShowGroupsTab,
    loadGroupMembers,
  ]);

  useEffect(() => {
    // Analytics embeds load their own queue/group data. Their parent already
    // loaded the overview summary, so repeating it here adds an expensive
    // database aggregation without contributing to either embedded view.
    if (!accounts.length || isQueueEmbed || isGroupsEmbed) {
      return;
    }

    const controller = new AbortController();
    loadSummary(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, isGroupsEmbed, isQueueEmbed, loadSummary]);

  useEffect(() => {
    if (!showGroupsTab && activeTab === "groups") {
      setActiveTab("pending");
      setSelectedGroup(null);
    }
  }, [showGroupsTab, activeTab]);

  useEffect(() => {
    if (!showOverviewTab && activeTab === "overview") {
      setActiveTab("pending");
    }
  }, [showOverviewTab, activeTab]);

  const handleSearch = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      showToast("From Date cannot be after To Date", "error");
      return;
    }

    setPage(1);
    setVerifiedPage(1);

    setAppliedSenderEmail(senderEmail.trim());
    setAppliedFromDate(fromDate ? new Date(fromDate).toISOString() : "");

    setAppliedToDate(toDate ? new Date(toDate).toISOString() : "");
  };

  const selectInboxTab = (tab) => {
    setActiveTab(tab);
    if (tab !== "groups") {
      setSelectedGroup(null);
      setSelectedGroupPerson(null);
    }
    if (tab === "verified") {
      setOlderThan24h(false);
    }
  };

  const applyListingFilter = ({
    assigned_state = "all",
    older_than_24h = false,
    department_id,
    assignee_email,
    queueTab,
  } = {}) => {
    setAssignedState(assigned_state);
    setOlderThan24h(Boolean(older_than_24h));
    if (department_id !== undefined) {
      setPendingDepartmentId(
        department_id === "unassigned" ? "" : department_id,
      );
    }
    if (assignee_email !== undefined) {
      setAssigneeEmail(assignee_email);
    }
    setPage(1);
    setVerifiedPage(1);
    const nextTab =
      queueTab || (activeTab === "overview" ? "pending" : null);
    if (nextTab) {
      selectInboxTab(nextTab);
    }
  };

  const applyQuickFilter = (filter) => {
    if (filter.tab === "verified") {
      selectInboxTab("verified");
      return;
    }

    const isActive = isPendingQuickFilterActive(filter, {
      assignedState,
      olderThan24h,
      activeTab,
    });

    if (filter.id !== "all" && isActive) {
      applyListingFilter({ assigned_state: "all", older_than_24h: false });
      return;
    }

    applyListingFilter({
      assigned_state: filter.assigned_state || "all",
      older_than_24h: Boolean(filter.older_than_24h),
    });
  };

  const applyKpiCardFilter = (key) => {
    const deptVerified = String(key).match(/^dept_verified_(.+)$/);
    if (deptVerified) {
      applyListingFilter({
        department_id: deptVerified[1],
        queueTab: "verified",
      });
      return;
    }
    if (
      key === "verified" ||
      key === "verified_today" ||
      key === "period" ||
      key === "my_verified" ||
      key === "team_verified"
    ) {
      applyListingFilter({
        assigned_state: key === "my_verified" ? "mine" : "all",
        queueTab: "verified",
      });
      return;
    }
    if (key === "unassigned") {
      applyListingFilter({ assigned_state: "unassigned", queueTab: "pending" });
      return;
    }
    if (key === "assigned") {
      applyListingFilter({ assigned_state: "assigned", queueTab: "pending" });
      return;
    }
    if (key === "older" || key === "my_older") {
      applyListingFilter({ older_than_24h: true, queueTab: "pending" });
      return;
    }
    if (key === "my_pending") {
      applyListingFilter({ assigned_state: "mine", queueTab: "pending" });
      return;
    }
    if (key === "team_pending" || key === "pending" || key === "in_progress") {
      applyListingFilter({ assigned_state: "all", queueTab: "pending" });
    }
  };

  useEffect(() => {
    if (!accounts.length || activeTab !== "verified") {
      return;
    }

    const controller = new AbortController();

    loadVerifiedData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, activeTab, loadVerifiedData]);

  useEffect(() => {
    setSelectedRow(null);
  }, [activeTab]);

  useEffect(() => {
    setSelectedRow(null);
  }, [page, verifiedPage]);

  const scrollToQueueTop = useCallback(() => {
    const target = queueTopRef.current;
    if (!target || reviewMode) return;

    const scrollParent = target.closest(".app-shell__content");
    if (scrollParent) {
      const top =
        target.getBoundingClientRect().top -
        scrollParent.getBoundingClientRect().top +
        scrollParent.scrollTop;
      scrollParent.scrollTo({ top, behavior: "auto" });
      return;
    }

    target.scrollIntoView({ block: "start" });
  }, [reviewMode]);

  useEffect(() => {
    if (skipInitialPageScroll.current) {
      skipInitialPageScroll.current = false;
      return;
    }

    scrollToQueueTop();
  }, [page, verifiedPage, scrollToQueueTop]);

  useEffect(() => {
    setSelectedRow(null);
  }, [appliedSenderEmail, appliedFromDate, appliedToDate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [reviewMode]);

  /* =========================================
     GROUP PENDING ATTACHMENTS
  ========================================= */
  const getFileNameFromPath = (path) => {
    if (!path) return "";

    const parts = path.split("/");

    return parts[parts.length - 1];
  };
  const grouped = useMemo(() => {
    const map = {};

    rows.forEach((r) => {
      if (!map[r.msg_id]) {
        map[r.msg_id] = {
          ...r,
          attachments: [],
        };

        /*
      Add email (.eml) as first attachment
    */
        if (r.email_storage_path) {
          map[r.msg_id].attachments.push({
            name: getFileNameFromPath(r.email_storage_path),
            path: r.email_storage_path,
            type: "email",
          });
        }
      }

      if (r.attachment_name) {
        map[r.msg_id].attachments.push({
          name: r.attachment_name,
          path: r.attachment_storage_path,
          type: "attachment",
        });
      }
    });

    return Object.values(map);
  }, [rows]);

  /* =========================================
     GROUP VERIFIED ATTACHMENTS
  ========================================= */

  const verifiedGrouped = useMemo(() => {
    const map = {};

    verifiedRows.forEach((r) => {
      if (!map[r.msg_id]) {
        map[r.msg_id] = {
          ...r,
          attachments: [],
        };

        if (r.email_storage_path) {
          map[r.msg_id].attachments.push({
            name: getFileNameFromPath(r.email_storage_path),
            path: r.email_storage_path,
            type: "email",
          });
        }
      }

      if (r.attachment_name) {
        map[r.msg_id].attachments.push({
          name: r.attachment_name,
          path: r.attachment_storage_path,
          type: "attachment",
        });
      }
    });

    return Object.values(map);
  }, [verifiedRows]);

  /* =========================================
     HELPERS
  ========================================= */

  const formatDate = useCallback((date) => {
    if (!date) return "—";

    return new Date(date).toLocaleString();
  }, []);

  const totalPages = Math.ceil(total / pendingPageSize);

  const verifiedTotalPages = Math.ceil(verifiedTotal / verifiedPageSize);

  /* =========================================
     ACTIVE COUNT
  ========================================= */

  const viewingGroupPeople =
    groupsTabActive &&
    Boolean(selectedGroup) &&
    selectedGroup.department_id !== "unassigned" &&
    !selectedGroupPerson;
  const viewingGroupQueue =
    groupsTabActive &&
    Boolean(selectedGroup) &&
    (selectedGroup.department_id === "unassigned" || Boolean(selectedGroupPerson));
  const viewingPendingQueue = activeTab === "pending" || viewingGroupQueue;
  const pendingEmpty = inboxQueueEmptyCopy({
    roleCode: hierarchyRoleCode,
    isAdmin,
    verified: false,
  });
  const verifiedEmpty = inboxQueueEmptyCopy({
    roleCode: hierarchyRoleCode,
    isAdmin,
    verified: true,
  });
  const pendingRowsActive = viewingPendingQueue || viewingGroupPeople;
  const activeRows = pendingRowsActive ? grouped : verifiedGrouped;
  const currentIndex = activeRows.findIndex(
    (x) => x.msg_id === selectedRow?.msg_id,
  );
  const handlePreview = useCallback(async (path) => {
    /*
    Prevent preview spam / double clicks
  */

    if (previewLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setPreviewLoading(true);

      const cleaned = path?.trim();

      if (!cleaned) {
        return;
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const lower = filePath.toLowerCase();

      const isTiff = lower.endsWith(".tif") || lower.endsWith(".tiff");

      const isEml = lower.endsWith(".eml");

      const isHeic = lower.endsWith(".heic") || lower.endsWith(".heif");

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;

      /* =========================
       TIFF PREVIEW
    ========================= */

      if (isTiff || isEml || isHeic) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });

        const response = await fetch(
          `${baseUrl}/api/adls/preview` +
          `?filesystem=${encodeURIComponent(filesystem)}` +
          `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to preview file (${response.status})`);
        }

        const blob = await response.blob();

        if (!blob || blob.size === 0) {
          throw new Error("Empty preview response");
        }

        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, "_blank", "noopener,noreferrer");

        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);

        return;
      }
      /* =========================
       NORMAL FILES
    ========================= */

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        {
          signal: controller.signal,

          params: {
            filesystem,
            path: filePath,
          },
        },
      );

      if (!res?.data?.url) {
        throw new Error("Preview URL missing");
      }

      /*
      Safe window open
    */

      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Preview failed", err);

      showToast?.(err?.message || "Failed to preview document", "error");
    } finally {
      controller.abort();

      setPreviewLoading(false);
    }
  }, [accounts, instance, previewLoading, showToast, FILESYSTEM]);

  const pendingKpiCards = buildInboxKpiCards({
    summary,
    roleCode: hierarchyRoleCode,
    isAdmin,
    tab: "pending",
  });
  const verifiedKpiCards = buildInboxKpiCards({
    summary,
    roleCode: hierarchyRoleCode,
    isAdmin,
    tab: "verified",
  });
  const actionItems = buildPendingActionItems({
    summary,
    layout: kpiLayout,
  });
  const quickFilters =
    activeTab === "verified"
      ? verifiedQuickFilters(kpiLayout)
      : pendingQuickFilters(kpiLayout);
  const pendingDepartments = summary.departments || [];
  const scopedDepartments = namedDepartments(pendingDepartments);
  const verifiedDepartments = scopedDepartments;
  const teamPeople = summary.people || [];

  /* =========================================
     RENDER TABLE
  ========================================= */

  const handleReindex = useCallback(async (msgId) => {
    try {
      await callApi(
        instance,
        accounts,
        "PUT",
        `/api/metadata/reindex/${msgId}`,
      );

      showToast("Reindex triggered successfully", "success");

      loadVerifiedData();
    } catch (err) {
      console.error("Reindex failed", err);

      showToast("Failed to reindex metadata", "error");
    }
  }, [accounts, instance, loadVerifiedData, showToast]);

  const goNext = () => {
    if (currentIndex < activeRows.length - 1) {
      const nextRow = activeRows[currentIndex + 1];

      setSelectedRow(nextRow);

      const rowKey = `${activeTab}-${nextRow.msg_id}`;

      setExpandedRows((prev) => ({
        ...prev,
        [rowKey]: true,
      }));
    }
  };

  const goPrevious = () => {
    if (currentIndex > 0) {
      const prevRow = activeRows[currentIndex - 1];

      setSelectedRow(prevRow);

      const rowKey = `${activeTab}-${prevRow.msg_id}`;

      setExpandedRows((prev) => ({
        ...prev,
        [rowKey]: true,
      }));
    }
  };

  /* =========================================
     MOBILE CARD/DRAWER MAPPERS
  ========================================= */

  const mapPendingCard = useCallback(
    (row) => ({
      id: `pending-${row.msg_id}`,
      icon: <Mail size={16} />,
      tone: "info",
      title: row.email_subject || "No subject",
      subtitle: row.mail_from_address || "—",
      meta: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        showInboxDepartmentColumn(kpiLayout, false)
          ? {
            label: "Department",
            value: listingDepartmentLabel(row) || "—",
          }
          : null,
        showAssignedColumn
          ? { label: "Assigned", value: formatAssignedTo(row.assigned_to) }
          : null,
        { label: "Entry", value: formatDate(row.created_at) },
      ].filter(Boolean),
      badges: row.status
        ? [
          {
            label: "Status",
            value: row.status,
            tone: "success",
          },
        ]
        : undefined,
      attachmentsCount: row.attachments?.length ?? 0,
      actions: reviewMode
        ? undefined
        : [
          {
            label: "Edit",
            onClick: () => {
              setSelectedRow(row);
              setReviewMode(true);
              setExpandedRows((prev) => ({
                ...prev,
                [`pending-${row.msg_id}`]: true,
              }));
            },
            icon: <Pencil size={14} />,
          },
        ],
    }),
    [formatDate, kpiLayout, reviewMode, setExpandedRows, setReviewMode, setSelectedRow, showAssignedColumn],
  );

  const mapPendingDrawer = useCallback(
    (row) => ({
      title: row.email_subject || "No subject",
      fields: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        { label: "Sender", value: row.mail_from_address || "—" },
        showInboxDepartmentColumn(kpiLayout, false)
          ? { label: "Department", value: listingDepartmentLabel(row) || "—" }
          : null,
        showAssignedColumn
          ? { label: "Assigned", value: formatAssignedTo(row.assigned_to) }
          : null,
        { label: "Entry Date & Time", value: formatDate(row.created_at) },
        { label: "Subject", value: row.email_subject || "—" },
      ].filter(Boolean),
      attachments:
        row.attachments?.map((a) => ({
          label: a.name,
          onPreview: () => handlePreview(a.path),
        })) || [],
      primaryAction: reviewMode
        ? null
        : {
          label: "Edit",
          onClick: () => {
            setSelectedRow(row);
            setReviewMode(true);
            setExpandedRows((prev) => ({
              ...prev,
              [`pending-${row.msg_id}`]: true,
            }));
          },
        },
    }),
    [
      formatDate,
      handlePreview,
      kpiLayout,
      reviewMode,
      setExpandedRows,
      setReviewMode,
      setSelectedRow,
      showAssignedColumn,
    ],
  );

  const mapVerifiedCard = useCallback(
    (row) => ({
      id: `verified-${row.msg_id}`,
      icon: <Files size={16} />,
      tone: "success",
      title: row.email_subject || "No subject",
      subtitle: row.mail_from_address || "—",
      meta: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        showInboxDepartmentColumn(kpiLayout)
          ? {
            label: "Department",
            value: listingDepartmentLabel(row) || "—",
          }
          : null,
        showAssignedColumn
          ? { label: "Assigned", value: formatAssignedTo(row.assigned_to) }
          : null,
        { label: "Verified By", value: row.verified_by || row.modified_by || "System" },
        { label: "Entry", value: formatDate(row.created_at) },
      ].filter(Boolean),
      attachmentsCount: row.attachments?.length ?? 0,
      actions: [
        {
          label: "Reindex",
          variant: "secondary",
          onClick: () => handleReindex(row.msg_id),
          icon: <RefreshCw size={14} />,
        },
      ],
    }),
    [formatDate, handleReindex, kpiLayout, showAssignedColumn],
  );

  const mapVerifiedDrawer = useCallback(
    (row) => ({
      title: row.email_subject || "No subject",
      fields: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        { label: "Sender", value: row.mail_from_address || "—" },
        showInboxDepartmentColumn(kpiLayout)
          ? { label: "Department", value: listingDepartmentLabel(row) || "—" }
          : null,
        showAssignedColumn
          ? { label: "Assigned", value: formatAssignedTo(row.assigned_to) }
          : null,
        {
          label: "Verified By",
          value: row.verified_by || row.modified_by || row.updated_by || "System",
        },
        { label: "Entry Date & Time", value: formatDate(row.created_at) },
        { label: "Subject", value: row.email_subject || "—" },
      ].filter(Boolean),
      attachments:
        row.attachments?.map((a) => ({
          label: a.name,
          onPreview: () => handlePreview(a.path),
        })) || [],
      primaryAction: {
        label: "Reindex",
        onClick: () => handleReindex(row.msg_id),
      },
    }),
    [formatDate, handlePreview, handleReindex, kpiLayout, showAssignedColumn],
  );

  const renderTable = (
    data,
    isLoading,
    currentPage,
    currentTotalPages,
    currentTotal,
    emptyTitle,
    emptySub,
    verified = false,
  ) => {
    const showDept = showInboxDepartmentColumn(kpiLayout);
    const colSpan =
      4 +
      (showDept ? 1 : 0) +
      (showAssignedColumn ? 1 : 0) +
      (reviewMode ? 0 : 1);

    return (
      <>
        <div className="ir-queue" style={{ ...styles.card, position: "relative" }}>
          <LoadingOverlay show={isLoading} label="Loading metadata..." />

          <div className="ir-queue__header" style={styles.cardHeader}>
            <div style={styles.cardTitle}>
              <Files size={18} />

              {pendingQueueTitle(kpiLayout, { verified, managerView })}
            </div>
            <div className="ir-count">{currentTotal} records</div>

          </div>

          <ScrollableTable
            style={{
              ...styles.tableWrapper,
              ...(isLoading && data.length === 0 ? { minHeight: 180 } : {}),
              ...(isLoading ? { opacity: 0.45 } : {}),
            }}
          >
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                    Metadata
                  </th>

                  <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                    Email Subject
                  </th>

                  <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                    Sender
                  </th>

                  {showDept && (
                    <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                      Department
                    </th>
                  )}

                  {showAssignedColumn && (
                    <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                      Assigned To
                    </th>
                  )}

                  {verified && (
                    <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                      Verified By
                    </th>
                  )}

                  {!verified && (
                    <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                      Att.
                    </th>
                  )}

                  {!reviewMode && <th style={{ ...styles.thRight, ...(isCompactTable ? styles.thRightCompact : {}) }}>
                    Action
                  </th>}
                </tr>
              </thead>

              <tbody>
                {!isLoading && data.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} style={styles.empty}>
                      <div style={styles.emptyState}>
                        <Files size={42} strokeWidth={1.5} />

                        <div style={styles.emptyTitle}>{emptyTitle}</div>

                        <div style={styles.emptySub}>{emptySub}</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row) => {
                    const rowKey = `${verified ? "verified" : "pending"}-${row.msg_id}`;

                    const isExpanded = Boolean(expandedRows[rowKey]);
                    const departmentLabel = listingDepartmentLabel(row);

                    return (
                      <React.Fragment key={rowKey}>
                        {/* ================= MAIN ROW ================= */}

                        <tr style={styles.row}>
                          <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                            <div style={styles.metaBlock}>
                              <span className={`ir-type ir-type--${metadataPillTone(row.document_type)}`}>
                                {row.document_type || "UNKNOWN"}
                              </span>
                              <div style={styles.metaTitle}>
                                {row.policy_number || "N/A"}
                              </div>
                              <div style={styles.metaSub}>
                                <Clock3 size={13} />
                                {formatDate(row.created_at)}
                              </div>
                            </div>
                          </td>

                          <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                            <div style={{ ...styles.subject, ...(isCompactTable ? styles.subjectCompact : {}) }}>
                              <span style={styles.subjectIcon}>
                                <Mail size={14} />
                              </span>

                              <span style={{ ...styles.subjectText, ...(isCompactTable ? styles.subjectTextCompact : {}) }}>
                                {row.email_subject || "—"}
                              </span>
                            </div>
                          </td>

                          <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                            <div style={{ ...styles.sender, ...(isCompactTable ? styles.senderCompact : {}) }} className="ir-sender-link">
                              {row.mail_from_address || "—"}
                            </div>
                          </td>

                          {showDept && (
                            <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                              {departmentLabel ? (
                                <span className={`ir-dept-pill ir-dept-pill--${departmentTone({ department_name: departmentLabel, department_id: row.department_id })}`}>
                                  {departmentLabel}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}

                          {showAssignedColumn && (
                            <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                              <AssignedCell value={row.assigned_to} />
                            </td>
                          )}

                          {/* ================= VERIFIED ================= */}

                          {verified ? (
                            <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                              <AssignedCell
                                value={
                                  row.verified_by ||
                                  row.modified_by ||
                                  row.updated_by ||
                                  "System"
                                }
                              />
                            </td>
                          ) : (
                            <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                              {row.attachments.length === 0 ? (
                                <span style={styles.noAttachment}>
                                  —
                                </span>
                              ) : (
                                <button
                                  className="ir-attach"
                                  type="button"
                                  style={styles.attachmentCountBtn}
                                  aria-expanded={isExpanded}
                                  onClick={() =>
                                    setExpandedRows((prev) => ({
                                      ...prev,
                                      [rowKey]: !prev[rowKey],
                                    }))
                                  }
                                >
                                  <FileText size={14} />
                                  {row.attachments.length}
                                </button>
                              )}
                            </td>
                          )}

                          {/* ================= ACTION ================= */}

                          <td style={{ ...styles.tdRight, ...(isCompactTable ? styles.tdRightCompact : {}) }}>
                            {verified ? (
                              <button
                                title="ReIndex Metadata"
                                style={styles.updateBtn}
                                onClick={() => handleReindex(row.msg_id)}
                              >
                                <RefreshCw size={14} />
                              </button>
                            ) : !reviewMode ? (
                              <button
                                title="Edit and Verify Metadata"
                                style={styles.updateBtn}
                                onClick={() => {
                                  setSelectedRow(row);

                                  setReviewMode(true);

                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [rowKey]: true,
                                  }));
                                }}
                              >
                                <Pencil size={14} />
                              </button>
                            ) : null}
                          </td>
                        </tr>

                        {/* ================= ATTACHMENTS ROW ================= */}

                        {!verified &&
                          isExpanded &&
                          row.attachments.length > 0 && (
                            <tr>
                              <td colSpan={colSpan} style={styles.attachmentRowTd}>
                                <div style={styles.attachmentPanel}>
                                  {row.attachments.map((a, idx) => (
                                    <div key={idx} style={styles.attachmentCard}>
                                      <div style={styles.attachmentCardLeft}>
                                        <FileText size={16} />

                                        <div style={styles.attachmentDetails}>
                                          <div style={styles.attachmentName}>
                                            {a.name}
                                          </div>
                                        </div>
                                      </div>

                                      <button
                                        title="Preview Document"
                                        style={styles.previewBtn}
                                        onClick={() => handlePreview(a.path)}
                                      >
                                        <Eye size={14} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </ScrollableTable>
        </div>

        {!reviewMode && !isLoading && currentTotal > 0 && (
          <Pagination
            page={currentPage}
            totalPages={currentTotalPages || 1}
            totalRecords={currentTotal}
            pageSize={verified ? verifiedPageSize : pendingPageSize}
            currentPageCount={data.length}
            onPageChange={(nextPage) =>
              verified ? setVerifiedPage(nextPage) : setPage(nextPage)
            }
          />
        )}
      </>
    );
  };

  const renderResponsiveQueue = (
    data,
    isLoading,
    currentPage,
    currentTotalPages,
    currentTotal,
    emptyTitle,
    emptySub,
    verified = false,
  ) => (
    <ResponsiveTableCards
      breakpoint={1024}
      data={data}
      loading={isLoading}
      loadingLabel="Loading metadata..."
      emptyTitle={emptyTitle}
      emptySub={emptySub}
      listTitle={pendingQueueTitle(kpiLayout, { verified })}
      listSubtitle={
        verified
          ? kpiLayout === "member"
            ? "Items assigned to you"
            : kpiLayout === "manager"
              ? "Your team queue"
              : kpiLayout === "senior"
                ? "Your groups"
                : "Full organization queue"
          : kpiLayout === "member"
            ? "Items assigned to you"
            : kpiLayout === "manager"
              ? "Your team queue"
              : kpiLayout === "senior"
                ? "All groups"
                : "Full organization queue"
      }
      cardMapper={verified ? mapVerifiedCard : mapPendingCard}
      drawerMapper={verified ? mapVerifiedDrawer : mapPendingDrawer}
      pagination={
        currentTotal > 0
          ? {
            page: currentPage,
            totalPages: currentTotalPages || 1,
            totalRecords: currentTotal,
            pageSize: verified ? verifiedPageSize : pendingPageSize,
            currentPageCount: data.length,
            onPageChange: (nextPage) =>
              verified ? setVerifiedPage(nextPage) : setPage(nextPage),
          }
          : undefined
      }
      renderTable={() =>
        renderTable(
          data,
          isLoading,
          currentPage,
          currentTotalPages,
          currentTotal,
          emptyTitle,
          emptySub,
          verified,
        )
      }
    />
  );

  /* =========================================
     UI
  ========================================= */

  return (
    <div
      style={{
        ...styles.container,
        ...(isSmallScreen ? styles.containerNarrow : {}),
        ...(isGroupsEmbed || isQueueEmbed ? styles.containerEmbed : {}),
        ...(reviewMode && selectedRow ? styles.reviewContainerMode : {}),
      }}
    >
      {queueEmbedSurfaceVisible && (
        <div style={{ ...styles.header, ...(isSmallScreen ? styles.headerNarrow : {}) }}>
          <div>
            <button
              type="button"
              style={{ ...styles.groupBackBtn, marginBottom: "12px" }}
              onClick={onEmbedQueueBack}
            >
              ← {embedQueueBackLabel}
            </button>
            <h2 style={styles.title}>{embedQueueTitle}</h2>
            <p style={styles.sub}>Filtered metadata records</p>
          </div>
          <div style={styles.statsCard}>
            <div style={styles.statsValue}>
              {activeTab === "verified"
                ? verifiedLoading
                  ? <MetricSkel variant="value" />
                  : formatInboxCount(verifiedTotal)
                : loading
                  ? <MetricSkel variant="value" />
                  : formatInboxCount(total)}
            </div>
            <div style={styles.statsLabel}>
              {embedQueueCountLabel ||
                (activeTab === "verified"
                  ? "Completed Records"
                  : "Pending Records")}
            </div>
          </div>
        </div>
      )}

      {!reviewMode && !isGroupsEmbed && !isQueueEmbed && (
        <div style={{ ...styles.header, ...(isSmallScreen ? styles.headerNarrow : {}) }}>
          <div>
            <h2 style={styles.title}>Metadata Review</h2>

            <p style={styles.sub}>
              {inboxPageSubtitle(kpiLayout, activeTab)}
            </p>
            <div className="ir-scope">
              <Check size={14} />
              {inboxScopeLine(kpiLayout, summary)}
            </div>
          </div>
          {(activeTab === "pending" || activeTab === "verified") && (
            <div style={styles.statsCard}>
              <div style={styles.statsValue}>
                {summaryLoading ? (
                  <MetricSkel variant="value" />
                ) : (
                  formatInboxCount(
                    activeTab === "pending"
                      ? summary.pending
                      : summary.verified,
                  )
                )}
              </div>
              <div style={styles.statsLabel}>
                {activeTab === "pending"
                  ? "Pending Records"
                  : "Verified Records"}
              </div>
            </div>
          )}
        </div>
      )}

      {!reviewMode && !isGroupsEmbed && !isQueueEmbed && (
        <div style={{ ...styles.tabs, ...(isSmallScreen ? styles.tabsNarrow : {}) }}>
          {showOverviewTab && (
            <button
              className={`ir-tab${activeTab === "overview" ? " is-active" : ""}`}
              style={{
                ...styles.tabBtn,
                ...(isSmallScreen ? styles.tabBtnNarrow : {}),
                ...(activeTab === "overview" ? styles.activeTabBtn : {}),
              }}
              onClick={() => selectInboxTab("overview")}
            >
              Overview
            </button>
          )}
          <button
            className={`ir-tab${activeTab === "pending" ? " is-active" : ""}`}
            style={{
              ...styles.tabBtn,
              ...(isSmallScreen ? styles.tabBtnNarrow : {}),

              ...(activeTab === "pending" ? styles.activeTabBtn : {}),
            }}
            onClick={() => selectInboxTab("pending")}
          >
            Pending Review
          </button>

          <button
            className={`ir-tab${activeTab === "verified" ? " is-active" : ""}`}
            style={{
              ...styles.tabBtn,
              ...(isSmallScreen ? styles.tabBtnNarrow : {}),

              ...(activeTab === "verified" ? styles.activeTabBtn : {}),
            }}
            onClick={() => selectInboxTab("verified")}
          >
            Verified Records
          </button>

          {showGroupsTab && (
            <button
              className={`ir-tab${activeTab === "groups" ? " is-active" : ""}`}
              style={{
                ...styles.tabBtn,
                ...(isSmallScreen ? styles.tabBtnNarrow : {}),
                ...(activeTab === "groups" ? styles.activeTabBtn : {}),
              }}
              onClick={() => {
                setActiveTab("groups");
                setSelectedGroup(null);
                setSelectedGroupPerson(null);
              }}
            >
              Groups
            </button>
          )}
        </div>
      )}

      {!reviewMode && activeTab === "overview" && (
        <>
          <div className="ir-overview-section" aria-busy={summaryLoading}>
            <div className="ir-overview-section__title">Pending queue</div>
            <div className="ir-kpi-grid" style={styles.kpiGrid}>
              {pendingKpiCards.map((card) => {
                const tone = card.tone || KPI_TONE[card.key] || "pending";
                return (
                  <button
                    key={card.key}
                    type="button"
                    className={`ir-kpi ir-kpi--${tone}${summaryLoading ? "" : " is-clickable"}`}
                    disabled={summaryLoading}
                    onClick={() => applyKpiCardFilter(card.key)}
                  >
                    <div className="ir-kpi__value">
                      {summaryLoading ? <MetricSkel variant="value" /> : card.value}
                    </div>
                    <div className="ir-kpi__label">{card.label}</div>
                    <div className="ir-kpi__hint">{card.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="ir-overview-section" aria-busy={summaryLoading}>
            <div className="ir-overview-section__title">Verified records</div>
            <div className="ir-kpi-grid" style={styles.kpiGrid}>
              {verifiedKpiCards.map((card) => {
                const tone = card.tone || KPI_TONE[card.key] || "verified";
                return (
                  <button
                    key={card.key}
                    type="button"
                    className={`ir-kpi ir-kpi--${tone}${summaryLoading ? "" : " is-clickable"}`}
                    disabled={summaryLoading}
                    onClick={() => applyKpiCardFilter(card.key)}
                  >
                    <div className="ir-kpi__value">
                      {summaryLoading ? (
                        <MetricSkel variant="num" />
                      ) : (
                        card.value
                      )}
                    </div>
                    <div className="ir-kpi__label">{card.label}</div>
                    <div className="ir-kpi__hint">{card.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {!reviewMode && activeTab === "overview" && kpiLayout === "org" && (
        <div className="ir-pending-band">
          <div className="ir-panel" style={styles.pendingBandCard}>
            <div className="ir-panel__title" style={styles.pendingBandTitle}>Departments</div>
            <div className="ir-dept-mini-grid" aria-busy={summaryLoading}>
              {summaryLoading ? (
                [0, 1, 2].map((i) => (
                  <div
                    key={`dept-skel-${i}`}
                    className="ir-dept-tile ir-dept-tile--skel"
                  >
                    <MetricSkel variant="pill" />
                    <div className="ir-dept-tile__value">
                      <MetricSkel variant="value" />
                    </div>
                    <MetricSkel variant="hint" />
                  </div>
                ))
              ) : scopedDepartments.length === 0 ? (
                <div style={styles.pendingBandEmpty}>No departments in scope.</div>
              ) : (
                scopedDepartments.map((dept) => {
                  const active =
                    String(pendingDepartmentId) === String(dept.department_id);
                  const tone = departmentTone(dept);
                  return (
                    <button
                      key={String(dept.department_id)}
                      type="button"
                      className={`ir-dept-tile${active ? " is-active" : ""}`}
                      onClick={() =>
                        applyListingFilter({
                          department_id: active ? "" : dept.department_id,
                        })
                      }
                    >
                      <div className={`ir-dept-pill ir-dept-pill--${tone}`}>
                        {dept.department_name}
                      </div>
                      <div className="ir-dept-tile__value">
                        {formatInboxCount(dept.pending_count)}
                      </div>
                      <div className="ir-dept-tile__hint">
                        {formatInboxCount(dept.older_than_24h)} older than 24h
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="ir-panel ir-action-center" style={styles.pendingBandCard}>
            <div className="ir-action-center__title">
              <AlertTriangle size={16} />
              Action Center
            </div>
            <div className="ir-action-center__list" aria-busy={summaryLoading}>
              {actionItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`ir-action-row ir-action-row--${item.key}${summaryLoading ? " is-loading" : ""}`}
                  disabled={summaryLoading}
                  onClick={() =>
                    applyListingFilter({
                      assigned_state: item.assigned_state,
                      older_than_24h: item.older_than_24h,
                    })
                  }
                >
                  <span className={`ir-action-dot ir-action-dot--${item.key}`} />
                  <div className="ir-action-row__copy">
                    <div className="ir-action-row__label">{item.label}</div>
                    <div className="ir-action-row__hint">{item.hint}</div>
                  </div>
                  <div className="ir-action-row__value">
                    {summaryLoading ? <MetricSkel variant="row" /> : item.value}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!reviewMode && activeTab === "overview" && kpiLayout === "senior" && (
        <>
          <div style={styles.groupGrid} aria-busy={summaryLoading}>
            {summaryLoading
              ? [0, 1].map((i) => <DepartmentRichCardSkel key={`rich-skel-${i}`} />)
              : namedDepartments(pendingDepartments).map((dept) => (
                <DepartmentRichCard
                  key={String(dept.department_id)}
                  dept={dept}
                  onOpen={() =>
                    applyListingFilter({ department_id: dept.department_id })
                  }
                />
              ))}
          </div>
          <div className="ir-attention" style={{ marginTop: 16 }} aria-busy={summaryLoading}>
            <div className="ir-attention__title">
              <AlertTriangle size={16} />
              Needs Attention
            </div>
            {summaryLoading ? (
              [0, 1].map((i) => (
                <div key={`attn-skel-${i}`} className="ir-action-row is-loading">
                  <div>
                    <MetricSkel variant="pill" />
                    <div style={{ marginTop: 8 }}>
                      <MetricSkel variant="hint" />
                    </div>
                  </div>
                  <MetricSkel variant="row" />
                </div>
              ))
            ) : (
              <>
                {namedDepartments(pendingDepartments)
                  .filter((dept) => Number(dept.older_than_24h) > 0)
                  .map((dept) => (
                    <button
                      key={String(dept.department_id)}
                      type="button"
                      className="ir-action-row"
                      onClick={() =>
                        applyListingFilter({
                          department_id: dept.department_id,
                          older_than_24h: true,
                        })
                      }
                    >
                      <div>
                        <div style={styles.actionLabel}>{dept.department_name}</div>
                        <div style={styles.kpiHint}>
                          {formatInboxCount(dept.older_than_24h)} items older than 24h
                        </div>
                      </div>
                      <div className="ir-num--older">
                        {formatInboxCount(dept.older_than_24h)}
                      </div>
                    </button>
                  ))}
              </>
            )}
          </div>
        </>
      )}

      {!reviewMode && activeTab === "overview" && kpiLayout === "manager" && (
        <div className="ir-panel" style={{ marginBottom: 16 }}>
          <div className="ir-panel__title">
            <Users size={16} />
            My Team{namedDepartments(pendingDepartments)[0]
              ? ` — ${namedDepartments(pendingDepartments)[0].department_name}`
              : ""}
          </div>
          {summaryLoading ? (
            <table className="ir-workload-table" style={styles.table} aria-busy="true">
              <thead>
                <tr>
                  <th style={styles.th}>Member</th>
                  <th style={styles.th}>Pending</th>
                  <th style={styles.th}>Assigned</th>
                  <th style={styles.th}>Unassigned</th>
                  <th style={styles.th}>Older &gt;24h</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3].map((i) => (
                  <tr key={`team-skel-${i}`} style={styles.row}>
                    <td style={styles.td}>
                      <div className="ir-assigned">
                        <MetricSkel variant="avatar" />
                        <div>
                          <MetricSkel variant="pill" />
                          <div style={{ marginTop: 6 }}>
                            <MetricSkel variant="hint" />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}><MetricSkel variant="row" /></td>
                    <td style={styles.td}><MetricSkel variant="row" /></td>
                    <td style={styles.td}><MetricSkel variant="row" /></td>
                    <td style={styles.td}><MetricSkel variant="row" /></td>
                    <td style={styles.td}><MetricSkel variant="hint" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : teamPeople.length === 0 ? (
            <div style={styles.pendingBandEmpty}>No people in your team.</div>
          ) : (
            <table className="ir-workload-table" style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Member</th>
                  <th style={styles.th}>Pending</th>
                  <th style={styles.th}>Assigned</th>
                  <th style={styles.th}>Unassigned</th>
                  <th style={styles.th}>Older &gt;24h</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {teamPeople.map((person) => {
                  const isYou =
                    String(person.email || "").toLowerCase() ===
                    String(accounts[0]?.username || "").toLowerCase();
                  return (
                    <tr key={person.email} style={styles.row}>
                      <td style={styles.td}>
                        <div className="ir-assigned">
                          <InboxAvatar name={person.display_name || person.email} />
                          <div>
                            <div style={styles.actionLabel}>
                              {person.display_name || person.email}
                              {isYou ? " (You)" : ""}
                            </div>
                            <div style={styles.kpiHint}>{person.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span className="ir-num--pending">
                          {formatInboxCount(person.pending_count)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span className="ir-num--assigned">
                          {formatInboxCount(person.pending_count)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span className="ir-num--unassigned">
                          {formatDashCount(person.unassigned_count)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span className="ir-num--older">
                          {formatInboxCount(person.older_than_24h)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          className="ir-text-link"
                          style={styles.personNameBtn}
                          onClick={() =>
                            applyListingFilter({
                              assignee_email: person.email,
                              assigned_state: "all",
                              queueTab: "pending",
                            })
                          }
                        >
                          {isYou ? "My Queue" : "View Queue"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!reviewMode &&
        activeTab === "pending" &&
        kpiLayout === "member" &&
        Number(summary.older_than_24h) > 0 && (
          <div className="ir-aging">
            <span>
              You have {formatInboxCount(summary.older_than_24h)} items older
              than 24 hours. These items are at risk of delay. Review them first.
            </span>
            <button
              type="button"
              onClick={() => applyListingFilter({ older_than_24h: true })}
            >
              Show Aging Items
            </button>
          </div>
        )}

      {!reviewMode && !isQueueEmbed && (activeTab === "pending" || activeTab === "verified") && (
        <div className="ir-panel ir-filter" style={styles.filterCard}>
          {kpiLayout === "manager" && (
            <div className="ir-view-toggle">
              <button
                type="button"
                className={managerView === "mine" ? "is-active" : ""}
                onClick={() => {
                  setManagerView("mine");
                  applyListingFilter({ assigned_state: "mine" });
                }}
              >
                {activeTab === "verified" ? "My Verified" : "My Items"}
              </button>
              <button
                type="button"
                className={managerView === "team" ? "is-active" : ""}
                onClick={() => {
                  setManagerView("team");
                  applyListingFilter({ assigned_state: "all" });
                }}
              >
                {activeTab === "verified" ? "Team Verified" : "My Team"}
              </button>
            </div>
          )}
          {quickFilters.length > 0 && (
            <div style={styles.quickFilters}>
              {quickFilters.map((filter) => {
                const active = isPendingQuickFilterActive(filter, {
                  assignedState,
                  olderThan24h,
                  activeTab,
                });
                const count = quickFilterCountLabel(filter, summary);
                return (
                  <button
                    key={filter.id}
                    type="button"
                    className={`ir-pill${active ? " is-active" : ""}`}
                    style={{
                      ...styles.quickFilterBtn,
                      ...(active ? styles.quickFilterBtnActive : {}),
                    }}
                    onClick={() => applyQuickFilter(filter)}
                  >
                    {filter.label}
                    {count ? <span className="ir-pill-count">{count}</span> : null}
                  </button>
                );
              })}
            </div>
          )}
          <div style={styles.filterGrid}>
            <div style={styles.filterField}>
              <label style={styles.filterLabel}>Search</label>

              <input
                type="text"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                placeholder="Search by Email or Work Item ID"
                style={styles.filterInput}
              />
            </div>

            {(kpiLayout === "org" || kpiLayout === "senior") && (
              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Department</label>
                <select
                  value={
                    pendingDepartmentId === "unassigned"
                      ? ""
                      : String(pendingDepartmentId || "")
                  }
                  onChange={(e) =>
                    applyListingFilter({
                      department_id: e.target.value || "",
                    })
                  }
                  style={styles.filterInput}
                >
                  <option value="">All Departments</option>
                  {scopedDepartments.map((dept) => (
                    <option
                      key={String(dept.department_id)}
                      value={dept.department_id}
                    >
                      {dept.department_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(kpiLayout === "org" || kpiLayout === "senior") &&
              activeTab !== "verified" &&
              teamPeople.length > 0 && (
                <div style={styles.filterField}>
                  <label style={styles.filterLabel}>Assigned To</label>
                  <select
                    value={assigneeEmail}
                    onChange={(e) =>
                      applyListingFilter({
                        assignee_email: e.target.value || "",
                      })
                    }
                    style={styles.filterInput}
                  >
                    <option value="">All Users</option>
                    {teamPeople.map((person) => (
                      <option key={person.email} value={person.email}>
                        {person.display_name || person.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div style={styles.filterField}>
              <label style={styles.filterLabel}>From Date & Time</label>

              <input
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                style={styles.filterInput}
              />
            </div>

            <div style={styles.filterField}>
              <label style={styles.filterLabel}>To Date & Time</label>

              <input
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                style={styles.filterInput}
              />
            </div>

            <div style={styles.filterActions}>
              <button style={styles.searchBtn} onClick={handleSearch}>
                Search
              </button>

              <button
                style={styles.clearBtn}
                onClick={() => {
                  setSenderEmail("");
                  setFromDate("");
                  setToDate("");

                  setAppliedSenderEmail("");
                  setAppliedFromDate("");
                  setAppliedToDate("");
                  setAssignedState("all");
                  setOlderThan24h(false);
                  setAssigneeEmail("");
                  setPendingDepartmentId("");
                  setManagerView("team");

                  setPage(1);
                  setVerifiedPage(1);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {(groupsSurfaceVisible || queueEmbedSurfaceVisible) && viewingPendingQueue && (
        <div ref={queueTopRef}>
          {viewingGroupQueue && (
            <div style={styles.groupQueueHeader}>
              <button
                type="button"
                style={styles.groupBackBtn}
                onClick={() => {
                  setSelectedGroup(null);
                  setSelectedGroupPerson(null);
                  setGroupMembers([]);
                  setPage(1);
                  setRows([]);
                  setTotal(0);
                }}
              >
                {isGroupsEmbed ? embedGroupsBackLabel : "Back to groups"}
              </button>
              {selectedGroupPerson && (
                <button
                  type="button"
                  style={styles.groupBackBtn}
                  onClick={() => {
                    setSelectedGroupPerson(null);
                    setPage(1);
                    setRows([]);
                    setTotal(0);
                  }}
                >
                  Back to {selectedGroup.department_name}
                </button>
              )}
              <div style={styles.groupQueueMeta}>
                {selectedGroupPerson
                  ? `${selectedGroupPerson.display_name || selectedGroupPerson.email} · pending assigned to them`
                  : selectedGroup.department_id === "unassigned"
                    ? "Unassigned Work · tickets with no assignee"
                    : selectedGroup.department_name}
              </div>
            </div>
          )}
          {renderResponsiveQueue(
            grouped,
            loading,
            page,
            totalPages,
            total,
            pendingEmpty.title,
            pendingEmpty.sub,
            false,
          )}
        </div>
      )}

      {groupsSurfaceVisible && groupsTabActive && !selectedGroup && (
        <div ref={queueTopRef}>
          {groupsLoading ? (
            <div style={{ ...styles.card, ...styles.empty }}>
              <div style={styles.loaderContainer}>
                <div style={styles.loader} />
                Loading groups...
              </div>
            </div>
          ) : displayGroups.length === 0 ? (
            <div style={{ ...styles.card, ...styles.empty }}>
              <div style={styles.emptyState}>
                <Layers size={42} strokeWidth={1.5} />
                <div style={styles.emptyTitle}>No department groups</div>
                <div style={styles.emptySub}>
                  Departments in scope will appear here.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={styles.groupGrid}>
                {displayGroups.map((group) => (
                  <DepartmentRichCard
                    key={String(group.department_id)}
                    dept={group}
                    onOpen={() => {
                      setSelectedGroup(group);
                      setSelectedGroupPerson(null);
                      setPage(1);
                      setRows([]);
                    }}
                  />
                ))}
              </div>
              {kpiLayout === "senior" && (
                <div className="ir-attention" style={{ marginTop: 16 }}>
                  <div className="ir-attention__title">
                    <AlertTriangle size={16} />
                    Needs Attention
                  </div>
                  {displayGroups
                    .filter((dept) => Number(dept.older_than_24h) > 0)
                    .map((dept) => (
                      <button
                        key={String(dept.department_id)}
                        type="button"
                        className="ir-action-row"
                        onClick={() => {
                          setSelectedGroup(dept);
                          setSelectedGroupPerson(null);
                          setPage(1);
                          setRows([]);
                        }}
                      >
                        <div>
                          <div style={styles.actionLabel}>
                            {dept.department_name} has aging items
                          </div>
                          <div style={styles.kpiHint}>
                            {formatInboxCount(dept.older_than_24h)} items older than 24h
                          </div>
                        </div>
                        <div className="ir-num--older">
                          {formatInboxCount(dept.older_than_24h)}
                        </div>
                      </button>
                    ))}
                </div>
              )}
              {kpiLayout === "org" && displayGroups.length > 0 && (
                <div className="ir-panel ir-workload">
                  <div className="ir-panel__title">Group Workload Overview</div>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Department / Group</th>
                        <th style={styles.th}>Pending</th>
                        <th style={styles.th}>Verified</th>
                        <th style={styles.th}>Members</th>
                        <th style={styles.th}>Assigned %</th>
                        <th style={styles.th}>Older &gt;24h</th>
                        <th style={styles.th}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayGroups.map((group) => (
                        <tr key={String(group.department_id)} style={styles.row}>
                          <td style={styles.td}>
                            <span className={`ir-dept-pill ir-dept-pill--${departmentTone(group)}`}>
                              {group.department_name}
                            </span>
                          </td>
                          <td style={styles.td}><span className="ir-num--pending">{formatInboxCount(group.pending_count)}</span></td>
                          <td style={styles.td}><span className="ir-num--verified">{formatInboxCount(group.verified_count)}</span></td>
                          <td style={styles.td}>{formatInboxCount(group.member_count)}</td>
                          <td style={styles.td}>{`${Number(group.assigned_pct) || 0}%`}</td>
                          <td style={styles.td}><span className="ir-num--older">{formatInboxCount(group.older_than_24h)}</span></td>
                          <td style={styles.td}>
                            <button
                              type="button"
                              style={styles.personNameBtn}
                              onClick={() => {
                                setSelectedGroup(group);
                                setSelectedGroupPerson(null);
                                setPage(1);
                                setRows([]);
                              }}
                            >
                              View Queue
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {groupWorkload.length > 0 && (
                <div className="ir-panel ir-workload" style={styles.workloadCard}>
                  <div className="ir-panel__title" style={styles.pendingBandTitle}>
                    <Users size={16} />
                    {kpiLayout === "senior" ? "Team Member Workload" : "Team Workload Snapshot"}
                  </div>
                  <table className="ir-workload-table" style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Team Member</th>
                        {kpiLayout === "senior" && <th style={styles.th}>Department</th>}
                        <th style={styles.th}>Pending</th>
                        <th style={styles.th}>Assigned</th>
                        <th style={styles.th}>Unassigned</th>
                        <th style={styles.th}>Older &gt;24h</th>
                        {kpiLayout === "senior" && <th style={styles.th}>Load</th>}
                        <th style={styles.th}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupWorkload.map((person) => {
                        const maxPending = Math.max(
                          1,
                          ...groupWorkload.map((row) => Number(row.pending_count) || 0),
                        );
                        const width = Math.round(
                          ((Number(person.pending_count) || 0) / maxPending) * 100,
                        );
                        return (
                          <tr key={person.email} style={styles.row}>
                            <td style={styles.td}>
                              <div className="ir-assigned">
                                <InboxAvatar name={person.display_name || person.email} />
                                <div>
                                  <div style={styles.actionLabel}>
                                    {person.display_name || person.email}
                                  </div>
                                  <div style={styles.kpiHint}>{person.email}</div>
                                </div>
                              </div>
                            </td>
                            {kpiLayout === "senior" && (
                              <td style={styles.td}>
                                <span className={`ir-dept-pill ir-dept-pill--${departmentTone({ department_name: person.department_name })}`}>
                                  {person.department_name || "—"}
                                </span>
                              </td>
                            )}
                            <td style={styles.td}><span className="ir-num--pending">{formatInboxCount(person.pending_count)}</span></td>
                            <td style={styles.td}><span className="ir-num--assigned">{formatInboxCount(person.pending_count)}</span></td>
                            <td style={styles.td}>
                              <span className="ir-num--unassigned">
                                {formatDashCount(person.unassigned_count)}
                              </span>
                            </td>
                            <td style={styles.td}><span className="ir-num--older">{formatInboxCount(person.older_than_24h)}</span></td>
                            {kpiLayout === "senior" && (
                              <td style={styles.td}>
                                <div className="ir-progress">
                                  <div className="ir-progress__fill" style={{ width: `${width}%` }} />
                                </div>
                              </td>
                            )}
                            <td style={styles.td}>
                              <button
                                type="button"
                                className="ir-text-link"
                                style={styles.personNameBtn}
                                onClick={() => {
                                  const dept = groups.find(
                                    (group) =>
                                      String(group.department_name) ===
                                      String(person.department_name),
                                  );
                                  if (dept && dept.department_id !== "unassigned") {
                                    setSelectedGroup(dept);
                                    setSelectedGroupPerson(person);
                                    setPage(1);
                                    setRows([]);
                                  }
                                }}
                              >
                                View Queue
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {groupsSurfaceVisible && viewingGroupPeople && (
        <div ref={queueTopRef}>
          <div style={styles.groupQueueHeader}>
            <button
              type="button"
              style={styles.groupBackBtn}
              onClick={() => {
                setSelectedGroup(null);
                setSelectedGroupPerson(null);
                setGroupMembers([]);
                setPage(1);
                setRows([]);
                setTotal(0);
              }}
            >
              {isGroupsEmbed ? embedGroupsBackLabel : "Back to groups"}
            </button>
            <div style={styles.groupQueueMeta}>
              {selectedGroup.department_name}
              {` · ${selectedGroup.member_count || 0} ${Number(selectedGroup.member_count) === 1 ? "member" : "members"
                }`}
            </div>
          </div>
          <div className="ir-l1">
            <div style={styles.groupCardStats}>
              <div>
                <div style={styles.groupStatValue}>
                  {formatInboxCount(selectedGroup.pending_count)}
                </div>
                <div style={styles.groupStatLabel}>Pending</div>
              </div>
              <div>
                <div style={styles.groupStatValue}>
                  {formatInboxCount(selectedGroup.verified_count)}
                </div>
                <div style={styles.groupStatLabel}>Verified</div>
              </div>
              <div>
                <div style={styles.groupStatValue}>
                  {formatInboxCount(selectedGroup.older_than_24h)}
                </div>
                <div style={styles.groupStatLabel}>Older than 24h</div>
              </div>
            </div>
            <AssignedDonut pct={selectedGroup.assigned_pct} />
          </div>
          {groupMembersLoading ? (
            <div style={{ ...styles.card, ...styles.empty }}>
              <div style={styles.loaderContainer}>
                <div style={styles.loader} />
                Loading people...
              </div>
            </div>
          ) : groupMembers.length === 0 ? (
            <div style={{ ...styles.card, ...styles.empty }}>
              <div style={styles.emptyState}>
                <Users size={42} strokeWidth={1.5} />
                <div style={styles.emptyTitle}>No people in this group</div>
                <div style={styles.emptySub}>
                  Members in this department will appear here.
                </div>
              </div>
            </div>
          ) : (
            <div className="ir-workload-card" style={{ ...styles.card, overflow: "hidden" }}>
              <ScrollableTable style={styles.tableWrapper}>
                <table className="ir-workload-table" style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Pending</th>
                      <th style={styles.th}>Assigned</th>
                      <th style={styles.th}>Unassigned</th>
                      <th style={styles.th}>Older than 24h</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMembers.map((person) => (
                      <tr
                        key={person.email}
                        style={styles.row}
                      >
                        <td style={styles.td}>
                          <button
                            type="button"
                            className="ir-member"
                            onClick={() => {
                              setSelectedGroupPerson(person);
                              setPage(1);
                              setRows([]);
                            }}
                          >
                            <InboxAvatar name={person.display_name || person.email} />
                            <span>
                              <span className="ir-member__name">
                                {person.display_name || person.email}
                              </span>
                              <span className="ir-member__email">{person.email}</span>
                            </span>
                          </button>
                        </td>
                        <td style={styles.td}>
                          <span className="ir-num--pending">
                            {formatInboxCount(person.pending_count)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span className="ir-num--assigned">
                            {formatInboxCount(person.assigned_count)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span className="ir-num--unassigned">
                            {formatDashCount(person.unassigned_count)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span className="ir-num--older">
                            {formatInboxCount(person.older_than_24h)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollableTable>
            </div>
          )}
          <div style={{ marginTop: 24 }}>
            <div style={styles.groupQueueHeader}>
              <div style={styles.groupQueueMeta}>
                {selectedGroup.department_name} Pending Queue
                {` · ${total || 0} ${Number(total) === 1 ? "ticket" : "tickets"}`}
              </div>
            </div>
            {renderResponsiveQueue(
              grouped,
              loading,
              page,
              totalPages,
              total,
              pendingEmpty.title,
              pendingEmpty.sub,
              false,
            )}
          </div>
        </div>
      )}

      {!reviewMode && activeTab === "overview" && kpiLayout === "senior" && (
        <div className="ir-pending-band">
          <div className="ir-panel" style={styles.pendingBandCard}>
            <div className="ir-panel__title" style={styles.pendingBandTitle}>
              Verified by department
            </div>
            <div className="ir-dept-mini-grid" aria-busy={summaryLoading}>
              {summaryLoading ? (
                [0, 1].map((i) => (
                  <div
                    key={`verified-dept-skel-${i}`}
                    className="ir-dept-tile ir-dept-tile--skel"
                  >
                    <MetricSkel variant="pill" />
                    <div className="ir-rich__metrics">
                      <MetricSkel variant="num" />
                      <MetricSkel variant="num" />
                      <MetricSkel variant="num" />
                    </div>
                  </div>
                ))
              ) : verifiedDepartments.length === 0 ? (
                <div style={styles.pendingBandEmpty}>No departments in scope.</div>
              ) : (
                verifiedDepartments.map((dept) => {
                  const active =
                    String(pendingDepartmentId) === String(dept.department_id);
                  return (
                    <button
                      key={String(dept.department_id)}
                      type="button"
                      className={`ir-dept-tile ir-rich--${departmentTone(dept)}${active ? " is-active" : ""}`}
                      onClick={() =>
                        applyListingFilter({
                          department_id: active ? "" : dept.department_id,
                          queueTab: "verified",
                        })
                      }
                    >
                      <div className={`ir-rich__name`}>{dept.department_name}</div>
                      <div className="ir-rich__metrics">
                        <div>
                          <b className="ir-num--verified">{formatInboxCount(dept.verified_count)}</b>
                          <span>Verified</span>
                        </div>
                        <div>
                          <b className="ir-num--pending">{formatInboxCount(dept.pending_count)}</b>
                          <span>Pending</span>
                        </div>
                        <div>
                          <b>{formatInboxCount(dept.member_count)}</b>
                          <span>Members</span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {(queueEmbedSurfaceVisible || !reviewMode) && activeTab === "verified" && (
        <div ref={queueTopRef}>
          {renderResponsiveQueue(
            verifiedGrouped,
            verifiedLoading,
            verifiedPage,
            verifiedTotalPages,
            verifiedTotal,
            verifiedEmpty.title,
            verifiedEmpty.sub,
            true,
          )}
        </div>
      )}
      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {toast.message}
        </div>
      )}

      {reviewMode && selectedRow && (
        <div style={styles.reviewWorkspace}>
          {/* =====================================
        REVIEW HEADER
    ===================================== */}

          {/* =====================================
        REVIEW CONTENT
    ===================================== */}

          <div
            style={{
              ...styles.reviewContent,
              ...(isNarrow ? styles.reviewContentNarrow : null),
              ...(isSmallScreen ? styles.reviewContentSmall : null),
            }}
          >
            {/* LEFT SIDE */}

            <div
              style={{
                ...styles.reviewMain,
                ...(isNarrow ? styles.reviewMainNarrow : null),
                ...(isSmallScreen ? styles.reviewMainSmall : null),
              }}
              className="scrollable"
            >
              <ResponsiveTableCards
                breakpoint={1024}
                data={[selectedRow]}
                loading={false}
                emptyTitle="No record selected"
                emptySub=""
                listTitle={
                  activeTab === "verified"
                    ? "Verified Metadata Queue"
                    : "Pending Metadata Queue"
                }
                listSubtitle="Selected record for review"
                cardMapper={
                  activeTab === "verified" ? mapVerifiedCard : mapPendingCard
                }
                drawerMapper={
                  activeTab === "verified" ? mapVerifiedDrawer : mapPendingDrawer
                }
                renderTable={() =>
                  renderTable(
                    [selectedRow],
                    false,
                    1,
                    1,
                    1,
                    "",
                    "",
                    activeTab === "verified",
                  )
                }
              />
            </div>

            {/* RIGHT SIDE PANEL */}

            <div
              style={{
                ...styles.workspaceLayout,
                ...(isNarrow ? styles.workspaceLayoutNarrow : null),
                ...(isSmallScreen ? styles.workspaceLayoutSmall : null),
              }}
            >
              <MetadataWorkspacePanel
                row={selectedRow}
                activeTab={activeTab === "verified" ? "verified" : "pending"}
                isWorkPool={false}
                source="DOC_INDEX_HUB"
                onClose={() => {
                  setReviewMode(isQueueEmbed);
                  setSelectedRow(null);
                  setExpandedRows({});
                }}
                onPreview={handlePreview}
                previewLoading={previewLoading}
                onReindex={handleReindex}
                onSaved={() => {
                  const controller = new AbortController();

                  if (pendingRowsActive) {
                    loadData(controller.signal);
                  } else {
                    loadVerifiedData(controller.signal);
                  }
                }}
              />
            </div>
          </div>

          <div style={{ ...styles.reviewActionBar, ...(isNarrow ? styles.reviewActionBarNarrow : null) }}>
            {/* LEFT */}

            <button
              style={{
                ...styles.reviewNavBtn,
                opacity: currentIndex === 0 ? 0.5 : 1,
                cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              }}
              disabled={currentIndex === 0}
              onClick={goPrevious}
            >
              Previous
            </button>

            {/* RIGHT */}

            <button
              style={{
                ...styles.reviewNavBtn,
                opacity: currentIndex === activeRows.length - 1 ? 0.5 : 1,
                cursor:
                  currentIndex === activeRows.length - 1
                    ? "not-allowed"
                    : "pointer",
              }}
              disabled={currentIndex === activeRows.length - 1}
              onClick={goNext}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================
   STYLES
========================================= */

const styles = {
  reviewContainerMode: {
    height: "100vh",
    overflow: "hidden",
    padding: 0,
  },
  reviewWorkspace: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
  },

  reviewNavBtn: {
    height: "46px",
    minWidth: "130px",
    padding: "0 22px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
  },

  exitBtn: {
    height: "40px",
    padding: "0 18px",
    borderRadius: "10px",
    border: "none",
    background: "#dc2626",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
  },

  reviewContent: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    minHeight: 0,
  },

  reviewContentNarrow: {
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "14px 16px",
    gap: "14px",
    boxSizing: "border-box",
  },

  reviewContentSmall: {
    padding: "10px 12px",
    gap: "10px",
  },

  reviewMain: {
    flex: 1,
    overflowY: "auto",
    padding: "14px",
    minHeight: 0,
  },

  reviewMainNarrow: {
    flex: "none",
    overflowY: "auto",
    maxHeight: "42vh",
    padding: 0,
    minHeight: 0,
  },

  reviewMainSmall: {
    maxHeight: "38vh",
  },

  reviewFooter: {
    height: "72px",
    background: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    flexShrink: 0,
  },

  reviewFooterLeft: {
    display: "flex",
    gap: "12px",
  },
  reviewActionBar: {
    height: "84px",
    background: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 32px",
    flexShrink: 0,
  },

  reviewActionBarNarrow: {
    height: "64px",
    padding: "0 16px",
  },

  reviewCenterButtons: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  cancelReviewBtn: {
    height: "46px",
    minWidth: "140px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    fontWeight: "600",
    cursor: "pointer",
  },

  updateReviewBtn: {
    height: "46px",
    minWidth: "160px",
    borderRadius: "12px",
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
  },
  workspaceLayout: {
    width: "480px",
    minWidth: "480px",
    maxWidth: "480px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 1px 4px rgba(15,23,42,0.07)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: "1rem",
    alignSelf: "flex-start",
    maxHeight: "calc(100% - 2rem)",
  },

  workspaceLayoutNarrow: {
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    margin: 0,
    alignSelf: "stretch",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 1px 4px rgba(15, 23, 42, 0.07)",
    flex: "1 1 auto",
    overflow: "hidden",
    maxHeight: "none",
  },

  workspaceLayoutSmall: {
    borderRadius: "14px",
  },
  searchBtn: {
    height: "42px",
    padding: "10px 18px",
    border: "none",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    boxShadow: "0 8px 16px rgba(37, 99, 235, 0.25)",
  },
  container: {
    padding: "24px",
    background: "#f8fafc",
    minHeight: "80vh",
  },
  containerNarrow: {
    padding: "12px 12px 28px",
  },
  containerEmbed: {
    padding: "0 0 24px",
    minHeight: 0,
  },
  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 9999,
  },
  attachmentCountBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "0",
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  attachmentRowTd: {
    padding: "0",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  attachmentPanel: {
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  attachmentCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
  },

  attachmentCardLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },

  attachmentDetails: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  attachmentName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
    wordBreak: "break-word",
  },

  attachmentPath: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px",
    wordBreak: "break-all",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    gap: "20px",
  },
  headerNarrow: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },
  statsCard: {
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: "14px",
    padding: "14px 20px",
    minWidth: "160px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  statsValue: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#2563eb",
  },
  statsLabel: {
    fontSize: "13px",
    color: "#64748b",
    marginTop: "4px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  tabs: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
  },
  tabsNarrow: {
    flexWrap: "wrap",
    marginBottom: "20px",
    width: "100%",
  },

  tabBtn: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#475569",
    padding: "10px 20px",
    borderRadius: "999px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  },
  tabBtnNarrow: {
    flex: "1 1 48%",
    minWidth: 0,
    textAlign: "center",
  },

  activeTabBtn: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
    boxShadow: "0 8px 18px rgba(37, 99, 235, 0.28)",
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  },

  kpiCard: {
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: "14px",
    padding: "16px 18px",
    boxSizing: "border-box",
  },

  kpiValue: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
    lineHeight: 1.15,
  },

  kpiLabel: {
    marginTop: "8px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  kpiHint: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#64748b",
  },

  card: {
    background: "#ffffff",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.06)",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 20px",
    borderBottom: "1px solid #e2e8f0",
  },

  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: "600",
    color: "#0f172a",
  },

  pageInfo: {
    fontSize: "13px",
    color: "#64748b",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "auto",
  },

  th: {
    textAlign: "left",
    whiteSpace: "nowrap",
    background: "#f8fafc",
    padding: "14px 18px",
    fontSize: "11px",
    fontWeight: "800",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  thRight: {
    textAlign: "center",
    background: "#f8fafc",
    padding: "14px 18px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    transition: "background 0.2s ease",
  },

  td: {
    padding: "14px 18px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    overflow: "hidden",
    fontSize: "13px",
    textAlign: "left",
  },
  thCompact: {
    padding: "10px 12px",
    fontSize: "11px",
  },
  thRightCompact: {
    padding: "10px 12px",
    fontSize: "11px",
  },
  tdCompact: {
    padding: "12px 12px",
    fontSize: "13px",
    textAlign: "left",
  },
  metaTitle: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    overflowWrap: "anywhere",
  },

  tdRight: {
    padding: "18px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    textAlign: "center",
  },

  metaBlock: {
    minWidth: "220px",
    maxWidth: "320px",
    overflow: "hidden",
  },

  dot: {
    color: "#94a3b8",
  },

  metaSub: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#64748b",
    fontSize: "13px",
    marginTop: "8px",
  },

  subject: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    // lineHeight: "1.5", //no lineHeight here 
    minWidth: 0,
    maxWidth: "340px",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  subjectIcon: {
    display: 'flex',
    flexShrink: 0,
    marginTop: '2px',
  },

  subjectText: {
    lineHeight: 1.4,
    minWidth: 0,
    fontSize: '13px',
  },
  subjectCompact: {
    gap: "6px",
  },
  subjectTextCompact: {
    fontSize: "13px",
  },


  sender: {
    color: "#334155",
    wordBreak: "break-word",
    maxWidth: "180px",
  },
  senderCompact: {
    fontSize: "13px",
  },

  noAttachment: {
    color: "#94a3b8",
    fontSize: "13px",
  },

  attachmentRail: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    overflowX: "auto",
    overflowY: "hidden",
    maxWidth: "320px",
    paddingBottom: "2px",
    scrollbarWidth: "thin",
  },
  attachmentRailCompact: {
    gap: "6px",
    paddingBottom: "1px",
  },

  attachmentChip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    minWidth: "160px",
    maxWidth: "160px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "6px 8px",
    flexShrink: 0,
  },

  attachmentChipLeft: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    overflow: "hidden",
    flex: 1,
    color: "#334155",
  },

  attachmentChipText: {
    fontSize: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  previewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    border: "none",
    background: "#e0e7ff",
    color: "#2563eb",
    cursor: "pointer",
    flexShrink: 0,
  },

  updateBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0f172a",
    color: "#ffffff",
    border: "none",
    width: "36px",
    height: "36px",
    borderRadius: "999px",
    cursor: "pointer",
    boxShadow: "0 6px 14px rgba(15, 23, 42, 0.18)",
  },

  empty: {
    padding: "60px 20px",
    textAlign: "center",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    color: "#94a3b8",
  },

  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#334155",
  },

  emptySub: {
    fontSize: "14px",
    color: "#64748b",
  },

  loaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "#64748b",
  },

  loader: {
    width: "18px",
    height: "18px",
    border: "2px solid #dbeafe",
    borderTop: "2px solid #2563eb",
    borderRadius: "50%",
  },

  filterCard: {
    padding: "18px",
    marginBottom: "20px",
  },

  filterGrid: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },

  filterField: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 200px",
    minWidth: 0,
  },

  filterLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    textTransform: "uppercase",
  },

  filterInput: {
    height: "42px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "0 12px",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
  },

  filterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: "10px",
    flex: "0 0 auto",
    marginLeft: "auto",
  },

  clearBtn: {
    height: "42px",
    padding: "0 16px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "600",
  },

  groupGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "14px",
    alignItems: "stretch",
  },

  groupCard: {
    textAlign: "left",
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: "16px",
    padding: "18px 20px",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },

  groupCardUnassigned: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
  },

  groupCardName: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
  },

  groupCardCode: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#64748b",
  },

  groupCardStats: {
    display: "flex",
    gap: "18px",
    marginTop: "16px",
  },

  groupStatValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },

  groupStatLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  groupQueueHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },

  groupBackBtn: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#2563eb",
    padding: "8px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
  },

  groupQueueMeta: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "600",
  },

  groupLevelHeader: {
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: "16px",
    padding: "16px 18px",
    marginBottom: "16px",
  },

  workloadCard: {
    marginTop: "16px",
  },

  workloadTrack: {
    marginTop: "8px",
    height: "8px",
    background: "#e2e8f0",
    borderRadius: "999px",
    overflow: "hidden",
  },

  workloadFill: {
    height: "8px",
    background: "#2563eb",
    borderRadius: "999px",
  },

  personNameBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#2563eb",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left",
  },

  kpiCardButton: {
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  pendingBand: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
  },

  pendingBandCard: {
    minWidth: 0,
  },

  pendingBandTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#0f172a",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "12px",
  },

  pendingBandEmpty: {
    fontSize: "13px",
    color: "#64748b",
  },

  deptMiniGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "10px",
  },

  deptMiniCard: {
    textAlign: "left",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  deptMiniCardActive: {
    border: "1px solid #2563eb",
    background: "#eff6ff",
  },

  deptMiniName: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#334155",
    marginBottom: "6px",
  },

  actionList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
    textAlign: "left",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "10px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  actionLabel: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#0f172a",
  },

  actionValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },

  peopleList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "8px",
    maxHeight: "280px",
    overflowY: "auto",
  },

  peopleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
    textAlign: "left",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "10px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  peopleCounts: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    fontSize: "12px",
    fontWeight: "600",
    color: "#334155",
  },

  quickFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "16px",
  },

  quickFilterBtn: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#475569",
    padding: "8px 12px",
    borderRadius: "999px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    fontFamily: "inherit",
  },

  quickFilterBtnActive: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
    boxShadow: "0 6px 14px rgba(37, 99, 235, 0.25)",
  },
};
